import { agruparTrechos } from "../Constants/quote";

/**
 * Comparação "o que a tela mostra × o que o fornecedor cobra agora", em cotação APROVADA.
 *
 * ATENÇÃO AO QUE ESTÁ SENDO COMPARADO — corrigido em 2026-09-05, depois de o owner desconfiar.
 *
 * A primeira versão deste módulo dizia comparar "valor aprovado × valor de hoje". Estava errado, e
 * o nome dos campos mentia junto. O `POST /quote/retrieve` **JÁ recomputa `precoBRL` com o câmbio
 * do dia** a cada abertura, para toda cotação não encerrada — é a regra "câmbio por STATUS"
 * (`QuoteService.retrieve`, ~L181). Ou seja, o número que o painel exibe **não** é o da aprovação:
 * é a tarifa da aprovação convertida pelo câmbio de HOJE.
 *
 * O que cada lado é, então:
 *
 *   esquerda (`naTelaBRL`) = tarifa gravada na aprovação × câmbio de hoje  ← o que o painel mostra
 *   direita  (`hojeBRL`)   = tarifa consultada agora     × câmbio de hoje  ← o que o fornecedor cobra
 *
 * Como o câmbio é o MESMO dos dois lados, o delta isola exatamente uma coisa: **a mudança de
 * tarifa**. É essa a pergunta que o preview responde, e a única que sobra em aberto — o câmbio já
 * está resolvido pelo retrieve.
 *
 * Por isso o preview **não existe em `reservada`** — o BE removeu esse modo em 05/09 e passou a
 * devolver 409 ali: a tarifa está travada e o câmbio já vem vivo do retrieve, então os dois lados
 * seriam idênticos por construção. Pior, num item já pago o congelamento é por RESERVA
 * (`PaymentBooking.exchangeRateUsed`) e recomputar pela taxa de hoje contradiria o painel — o bug
 * que aquele congelamento existe para impedir, reintroduzido por uma porta nova.
 *
 * NUNCA sobrescrever os valores da tela com estes. O backend já teve esse bug: o re-cote rodava em
 * qualquer status e reabrir o link depois da venda sobrescrevia `precoUSD/precoBRL/disponivel`,
 * chegando a marcar como indisponível um item já reservado e pago.
 *
 * ---
 * TRÊS MOTIVOS DE RECUSA, NÃO UM (contrato do BE, 2026-09-04)
 *
 * O BE devolve `motivo` junto de `disponivel: false`, e a distinção é a razão de este módulo não
 * ser um `if`:
 *
 *   `indisponivel`      — o fornecedor não tem mais a tarifa. O item sumiu de verdade.
 *   `consulta_falhou`   — o fornecedor não respondeu. Isso é "NÃO SEI", não "sumiu". Tratar como
 *                         indisponível faria a tela dizer que um hotel acabou por causa de um
 *                         timeout — alarme falso no exato momento em que o operador decide.
 *   qualquer outro code — a tarifa EXISTE, mas o booking vai recusar: é o veredito da política de
 *                         cancelamento (BUG-004). O item aparece disponível no fornecedor e mesmo
 *                         assim não dá para reservar; sem separar, a tela contradiz o resultado.
 */

const centavos = (n) => Math.round((Number(n) || 0) * 100);
const doCentavo = (c) => c / 100;

/** Diferença relevante? Abaixo de 1 centavo é ruído de arredondamento, não mudança. */
const mudou = (a, b) => centavos(a) !== centavos(b);

export const MOTIVO_INDISPONIVEL = "indisponivel";
export const MOTIVO_CONSULTA_FALHOU = "consulta_falhou";

/** ok | indisponivel | bloqueado (política) | desconhecido (consulta falhou ou item ausente) */
const classificar = (p) => {
	if (!p) return "desconhecido";
	if (p.disponivel !== false) return "ok";
	if (p.motivo === MOTIVO_CONSULTA_FALHOU) return "desconhecido";
	if (!p.motivo || p.motivo === MOTIVO_INDISPONIVEL) return "indisponivel";
	return "bloqueado";
};

/**
 * @param {Array}  aprovados  `[{ id, hotel, precoUSD, precoBRL }]` — o que o cliente aprovou
 * @param {Object} preview    `{ itens: [{ id, precoUSD, precoBRL, disponivel, motivo }], cambio, modo }`
 * @returns {null|Object} null quando não há o que comparar
 */
export const compararValores = (aprovados, preview) => {
	if (!Array.isArray(aprovados) || !aprovados.length) return null;
	if (!preview || !Array.isArray(preview.itens)) return null;

	const porId = new Map(preview.itens.map((i) => [String(i.id), i]));

	const itens = aprovados.map((a) => {
		const p = porId.get(String(a.id)) || null;
		const situacao = classificar(p);
		const ok = situacao === "ok";

		const naTelaBRL = Number(a.precoBRL) || 0;
		const hojeBRL = ok ? Number(p.precoBRL) || 0 : null;
		const deltaBRL = hojeBRL === null ? null : doCentavo(centavos(hojeBRL) - centavos(naTelaBRL));

		return {
			id: a.id,
			hotel: a.hotel || "",
			naTelaBRL,
			tarifaNaTelaUSD: Number(a.precoUSD) || 0,
			hojeBRL,
			hojeUSD: ok ? Number(p.precoUSD) || 0 : null,
			deltaBRL,
			deltaPct: deltaBRL === null || naTelaBRL === 0 ? null : (deltaBRL / naTelaBRL) * 100,
			situacao,
			motivo: p ? p.motivo || null : null,
			// Separar "mudou a tarifa" de "mudou o câmbio" importa: em `reservada` a tarifa está
			// travada, então mudança em USD ali seria sintoma de problema, não de mercado.
			mudouTarifa: ok ? mudou(a.precoUSD, p.precoUSD) : false,
		};
	});

	const de = (s) => itens.filter((i) => i.situacao === s);
	const indisponiveis = de("indisponivel");
	const bloqueados = de("bloqueado");
	const desconhecidos = de("desconhecido");
	const comparaveis = de("ok");

	const totalNaTelaBRL = doCentavo(itens.reduce((s, i) => s + centavos(i.naTelaBRL), 0));
	// O total de hoje só soma o que dá para reservar hoje. Somar o preço antigo de um item que
	// sumiu produziria um total que ninguém consegue pagar.
	const totalHojeBRL = doCentavo(comparaveis.reduce((s, i) => s + centavos(i.hojeBRL), 0));
	const baseComparavel = doCentavo(comparaveis.reduce((s, i) => s + centavos(i.naTelaBRL), 0));
	const totalDeltaBRL = doCentavo(centavos(totalHojeBRL) - centavos(baseComparavel));

	return {
		itens,
		indisponiveis,
		bloqueados,
		desconhecidos,
		totalNaTelaBRL,
		totalHojeBRL,
		totalDeltaBRL,
		totalDeltaPct: baseComparavel === 0 ? null : (totalDeltaBRL / baseComparavel) * 100,
		// `parcial` avisa que o total de hoje NÃO cobre a cotação inteira — sem isso a soma menor
		// pareceria uma boa notícia, quando na verdade é item faltando.
		parcial: comparaveis.length !== itens.length,
		// Só o que IMPEDE reservar entra aqui. `desconhecidos` fica de fora de propósito: o
		// fornecedor não ter respondido não é motivo para dizer que algo mudou.
		temMudanca: totalDeltaBRL !== 0 || indisponiveis.length > 0 || bloqueados.length > 0,
		modo: preview.modo || null,
		cambio: preview.cambio || null,
	};
};

/** Rótulo curto do movimento, para a tela não repetir a mesma condição em cinco lugares. */
export const sinalDelta = (delta) => (delta === null ? null : delta > 0 ? "subiu" : delta < 0 ? "caiu" : "igual");

/**
 * Texto do porquê de um item não entrar no total. Os códigos de política vêm do BE (BUG-004) e
 * podem crescer — por isso há um caso geral em vez de um mapa fechado que silencia o desconhecido.
 */
export const motivoTexto = (item) => {
	if (!item) return "";
	switch (item.situacao) {
		case "indisponivel":
			return "não está mais disponível no fornecedor";
		case "desconhecido":
			return "não foi possível consultar agora — tente de novo em instantes";
		case "bloqueado":
			return item.motivo === "FREE_WINDOW_TOO_SHORT"
				? "a tarifa existe, mas a janela de cancelamento gratuito é curta demais — a reserva seria recusada"
				: item.motivo === "FREE_WINDOW_CLOSED"
					? "a tarifa existe, mas o prazo de cancelamento gratuito já passou — a reserva seria recusada"
					: "a tarifa existe, mas a reserva seria recusada pela política de cancelamento";
		default:
			return "";
	}
};

/**
 * Os itens que o cliente selecionou, no shape que `compararValores` espera.
 *
 * Mora aqui porque DOIS lugares precisam da mesma lista pelos mesmos ids: o toggle de conferência e
 * a trava do "Prosseguir com reserva". Duplicar a montagem convidaria as duas a divergirem — e
 * divergência de id aqui aparece como "não foi possível consultar", que é o sintoma mais enganoso
 * que já tivemos nesta feature.
 *
 * `opcoes` NÃO existe em `quote.trechos`: quem monta é `agruparTrechos`, a partir de `quote.itens`.
 */
export const selecionadosDaCotacao = (quote) => {
	const sel = quote?.selecaoCliente || {};
	return agruparTrechos(quote)
		.map((t) => (t.opcoes || []).find((o) => o.id === sel[t.id]))
		.filter(Boolean)
		.map((o) => ({ id: o.id, hotel: o.hotel?.nome || "", precoUSD: o.precoUSD, precoBRL: o.precoBRL }));
};

/**
 * O que IMPEDE reservar agora — some do caminho feliz e é exatamente o que o `proceed-reservation`
 * vai recusar, porque o preview aplica os mesmos dois gates (disponibilidade + política).
 *
 * `desconhecidos` fica FORA de propósito: o fornecedor não ter respondido não é motivo para barrar
 * o operador. Quem barra de verdade é o backend, que refaz a consulta na hora de reservar.
 */
export const impedimentosDoPreview = (comparacao) =>
	comparacao ? [...comparacao.indisponiveis, ...comparacao.bloqueados] : [];
